/** @format */
import { DataLayerCheckoutEventType } from '@toucan/types/analytics';
import {
    WarrantyProductsForReporting,
    WarrantyTrackingOutcome,
} from '@toucan/types/warranty';

import {
    itemsToDataLayer,
    trackEnhancedEcommerce,
    trackViaDataLayer,
} from '@toucan/helpers/analytics';

import {
    CheckoutType,
    PaymentGateway,
    PaymentGatewayType,
} from '@toucan/client/core/checkout';
import { ProductSummaryV2 } from '@toucan/client/global/product';
import {
    ProductItemSummary,
    calculateTotal,
} from '@toucan/client/global/transaction';
import { isUuidCheckoutId } from '@toucan/common/helpers/identifiers';

export function trackFinalPaymentMethod(option: string) {
    trackEnhancedEcommerce({
        event: 'checkoutOption',
        ecommerce: {
            checkout_option: {
                actionField: {
                    step: 7,
                    option,
                },
            },
        },
    });
}

export function trackPaymentMethodChange(
    paymentOption: string,
    itemsToBuy: ProductItemSummary[] | [],
    products: ProductSummaryV2[] | []
) {
    const checkoutPaymentOptionSelectedDatalayerPayload = {
        paymentOption,
        event: DataLayerCheckoutEventType.PAYMENT_OPTION_SELECTED,
        items: itemsToDataLayer(itemsToBuy, products),
        itemsBuyingTotalValue: calculateTotal(products),
    };
    trackViaDataLayer(checkoutPaymentOptionSelectedDatalayerPayload);
}

export function trackPaymentSectionHit() {
    trackEnhancedEcommerce({
        event: 'checkout',
        ecommerce: {
            checkout: {
                actionField: {
                    step: 7,
                },
            },
        },
    });
}

type PrePaymentTokenParams = {
    checkoutId: string;
};

type PrePaymentOptionParams = PrePaymentTokenParams & {
    paymentToken?: string;
};

type PostPaymentOptionParams = PrePaymentOptionParams & {
    type: CheckoutType;
};

export function trackPaymentGatewaysRequested() {
    return trackViaDataLayer({
        event: DataLayerCheckoutEventType.PAYMENT_GATEWAYS_REQUESTED,
    });
}

export function trackPaymentGateways(gateways: PaymentGateway[]) {
    const gateway = gateways[0];
    return trackViaDataLayer({
        event: DataLayerCheckoutEventType.PAYMENT_GATEWAYS,
        gateway: gateway?.name,
        methods: gateway?.methods,
    });
}

export function trackPaymentTokenRequested(gateway: PaymentGatewayType) {
    return trackViaDataLayer({
        event: DataLayerCheckoutEventType.PAYMENT_TOKEN_REQUESTED,
        gateway,
    });
}

export function trackPaymentToken({ paymentToken }: PrePaymentOptionParams) {
    return trackViaDataLayer({
        event: DataLayerCheckoutEventType.PAYMENT_TOKEN,
        paymentId: getPaymentIdFromToken(paymentToken),
    });
}

export function trackPaymentTokenError(gateway: PaymentGatewayType) {
    return trackViaDataLayer({
        event: DataLayerCheckoutEventType.PAYMENT_TOKEN_ERROR,
        gateway,
    });
}

export function trackStripeElementsLoaded({
    paymentToken,
    checkoutId,
}: PrePaymentOptionParams) {
    return trackViaDataLayer({
        event: DataLayerCheckoutEventType.STRIPE_ELEMENTS_LOADED,
        checkoutId,
        paymentId: getPaymentIdFromToken(paymentToken),
    });
}

export function trackPaymentOptionValid({
    paymentToken,
    type,
    checkoutId,
}: PostPaymentOptionParams) {
    return trackViaDataLayer({
        event: DataLayerCheckoutEventType.PAYMENT_OPTION_VALID,
        type,
        checkoutId,
        paymentId: getPaymentIdFromToken(paymentToken),
    });
}

export function trackConfirmPayment({
    paymentToken,
    type,
    checkoutId,
}: PostPaymentOptionParams) {
    return trackViaDataLayer({
        event: DataLayerCheckoutEventType.CONFIRM_PAYMENT,
        type,
        checkoutId,
        paymentId: getPaymentIdFromToken(paymentToken),
    });
}

export function trackPaymentComplete({
    type,
    checkoutId,
    paymentToken,
}: PostPaymentOptionParams) {
    return trackViaDataLayer({
        event: DataLayerCheckoutEventType.PAYMENT_COMPLETE,
        type,
        checkoutId,
        paymentId: getPaymentIdFromToken(paymentToken),
    });
}

export function trackPaymentError({
    type,
    paymentToken,
    checkoutId,
    messageDisplayed,
    message,
    code,
}: PostPaymentOptionParams & {
    messageDisplayed?: string;
    message?: string;
    code?: string;
}) {
    return trackViaDataLayer({
        event: DataLayerCheckoutEventType.PAYMENT_ERROR,
        checkoutId,
        type,
        paymentId: getPaymentIdFromToken(paymentToken),
        messageDisplayed,
        message,
        code,
    });
}

export function trackCheckoutCreated({ checkoutId }: PrePaymentTokenParams) {
    const checkoutVersion = isUuidCheckoutId(checkoutId) ? 'new' : 'legacy';

    return trackViaDataLayer({
        event: DataLayerCheckoutEventType.CHECKOUT_CREATED,
        checkoutId,
        checkoutVersion,
    });
}

export function trackCheckoutStarted({
    paymentToken,
    type,
    checkoutId,
}: PostPaymentOptionParams) {
    return trackViaDataLayer({
        event: DataLayerCheckoutEventType.CHECKOUT_STARTED,
        type,
        checkoutId,
        paymentId: getPaymentIdFromToken(paymentToken),
    });
}

export function trackCheckoutComplete({
    paymentToken,
    type,
    checkoutId,
}: PostPaymentOptionParams) {
    return trackViaDataLayer({
        event: DataLayerCheckoutEventType.CHECKOUT_COMPLETE,
        checkoutId,
        type,
        paymentId: getPaymentIdFromToken(paymentToken),
    });
}

export function trackCheckoutUnexpectedError({
    paymentToken,
    type,
    checkoutId,
    errorId,
}: PrePaymentTokenParams &
    Partial<PostPaymentOptionParams> & { errorId: string }) {
    return trackViaDataLayer({
        event: DataLayerCheckoutEventType.UNEXPECTED_ERROR,
        checkoutId,
        type,
        paymentId: getPaymentIdFromToken(paymentToken),
        errorId,
    });
}

export function trackCheckoutFinalizeError({
    type,
    checkoutId,
    errorId,
}: Omit<PostPaymentOptionParams, 'paymentToken'> & { errorId: string }) {
    return trackViaDataLayer({
        event: DataLayerCheckoutEventType.FINALIZE_ERROR,
        checkoutId,
        type,
        errorId,
    });
}

export function trackCalcResponseInvalid({
    checkoutId,
}: PrePaymentTokenParams) {
    return trackViaDataLayer({
        event: DataLayerCheckoutEventType.CALC_RESPONSE_INVALID,
        checkoutId,
    });
}

export function trackCalcResponseRetried({
    checkoutId,
}: PrePaymentTokenParams) {
    return trackViaDataLayer({
        event: DataLayerCheckoutEventType.CALC_RESPONSE_RETRIED,
        checkoutId,
    });
}

export function trackCalcResponseRetryStatus({
    checkoutId,
    succeeded,
}: PrePaymentTokenParams & { succeeded: boolean }) {
    return trackViaDataLayer({
        event: DataLayerCheckoutEventType.CALC_RESPONSE_RETRY_STATUS,
        checkoutId,
        succeeded,
    });
}

export function getPaymentIdFromToken(paymentToken = '') {
    return paymentToken.replace(/_secret_.*/, '');
}

export function trackWarrantySectionCompletion(
    outcome: WarrantyTrackingOutcome,
    productsWithProtection: WarrantyProductsForReporting[] = []
) {
    return trackViaDataLayer({
        event: DataLayerCheckoutEventType.WARRANTY_COMPLETE,
        outcome,
        productsWithProtection,
    });
}
